/**
 * Tokens `{{ex:...}}` que injetam os valores MEDIDOS do painel "Exames do
 * atendimento" dentro do corpo de um laudo/declaração. É a ponte entre os
 * módulos estruturados (`EncounterExamsState`) e o texto livre — espelha a
 * mesma leitura de `serializeEncounterExams` (utils/encounterSerialize.ts) e
 * `readValue` (services/pdf/clinicalContext.ts). Os dois olhos saem em LINHAS
 * separadas — OD em cima, OE embaixo (pedido do dono, 26/10/2026): quebra
 * `<br>` quando o texto do modelo é HTML (editor formatado) e quebra de
 * linha comum quando é texto puro.
 *
 * DECISÃO DE DESIGN: o resolver é PURO e devolve TEXTO PURO (nunca HTML —
 * exceto o `<br>` entre os olhos, quando o próprio modelo já é HTML). Os
 * valores são "assados" (resolvidos literalmente) no momento em que o médico
 * carrega o modelo ou clica em "Preencher exames", gravando um snapshot —
 * diferente de `{{paciente}}` (estável, resolvido só na impressão), os valores
 * de exame são específicos da consulta e precisam virar texto fixo pra
 * sobreviver a salvar / reabrir / histórico / edição da IA.
 *
 * Sintaxe:
 *   {{ex:pio}}                      → módulo inteiro: "OD 14 mmHg⏎OE 16 mmHg"
 *   {{ex:avlcc:OD}}                 → 1º campo do módulo, um olho: "20/20"
 *   {{ex:biometria:al:OD}}          → campo nomeado + olho: "23.50 mm"
 *   {{ex:classificacao-glaucoma:classificacao}} → campo mono (sem olho)
 *
 * Os slugs (moduleId/fieldKey) são os ids ESTÁVEIS de `encounterCatalog.ts` —
 * mudar um id lá quebra tokens já salvos em modelos.
 */

import { BUILTIN_EXAM_MODULES, EXAM_MODULE_BY_ID } from '@/data/encounterCatalog';
import { formatModuleEye, formatModuleMonoFields } from '@/utils/encounterSerialize';
import { looksLikeHtml } from '@/utils/richText';
import type {
  EncounterExamsState,
  ExamFieldDef,
  ExamModuleEntry,
} from '@/types/encounter';

/** Placeholder pra campo vazio / módulo desconhecido — nunca deixa o token
 *  cru no documento. 4 underscores (mesmo padrão dos seeds de atestado). */
export const EMPTY_FIELD = '____';

/** Mesma forma de `TagDefinition` (DocumentEditor) — mantido local pra o util
 *  não depender de uma página. Consumido pelos chips do AttestationEditor. */
export interface ExamTagDefinition {
  label: string;
  tag: string;
  description: string;
}

/**
 * Regex de UM token. Grupos:
 *   1 = moduleId (kebab: minúsculas, dígitos, hífen)
 *   2 = opcional — fieldKey OU olho (OD/OE)
 *   3 = opcional — olho (OD/OE)
 * `g` pra trocar todos via String.replace(fn). Só usar em .replace (que
 * reseta lastIndex) — nunca em .test com este flag.
 */
const EXAM_TAG_RE = /\{\{ex:([a-z0-9-]+)(?::([a-zA-Z0-9_]+))?(?::(OD|OE|od|oe))?\}\}/g;

/** Quebra entre os olhos (e demais partes do módulo) no texto puro. */
export const EYE_BREAK = '\n';

/** Casa exatamente "OD"/"OE" (case-insensitive) — distingue olho de fieldKey. */
const EYE_RE = /^(OD|OE)$/i;

/**
 * Lê UM campo formatado (com sufixo). `eye` opcional restringe a OD/OE; sem
 * `eye`, campo `eyes` devolve "OD x" e "OE y" separados por `br` (só lados
 * preenchidos) e campo
 * `mono` devolve o valor único. Devolve '' (string vazia) quando não há valor
 * — quem chama decide trocar por `EMPTY_FIELD`.
 */
function readField(
  field: ExamFieldDef,
  entry: ExamModuleEntry | undefined,
  eye?: 'OD' | 'OE',
  br: string = EYE_BREAK,
): string {
  const suf = field.suffix ? ` ${field.suffix}` : '';
  const vals = entry?.values[field.key] ?? {};

  if ((field.layout ?? 'eyes') === 'mono') {
    return vals.mono?.trim() ? `${vals.mono.trim()}${suf}` : '';
  }
  if (eye === 'OD') return vals.OD?.trim() ? `${vals.OD.trim()}${suf}` : '';
  if (eye === 'OE') return vals.OE?.trim() ? `${vals.OE.trim()}${suf}` : '';

  const parts: string[] = [];
  if (vals.OD?.trim()) parts.push(`OD ${vals.OD.trim()}${suf}`);
  if (vals.OE?.trim()) parts.push(`OE ${vals.OE.trim()}${suf}`);
  return parts.join(br);
}

/**
 * Forma 1 — módulo inteiro, AGRUPADO POR OLHO.
 *
 * Módulo de 1 campo (ex.: pio) sai só com os valores ("OD 14 mmHg", e
 * "OE 16 mmHg" na linha de baixo). Módulo multi-campo sai com cada olho
 * INTEIRO numa linha ("OD K1 43 D · K2 44 D", depois "OE K1 44 D · K2 45 D")
 * em vez de campo a campo com os dois olhos dentro: a leitura clínica é por
 * olho, como no prontuário (`·` separa campos do mesmo olho). Campos sem olho
 * vêm depois, cada um na sua linha. `br` é a quebra de linha (`<br>` em HTML).
 */
export function formatExamModule(
  moduleId: string,
  state: EncounterExamsState,
  br: string = EYE_BREAK,
): string {
  const mod = EXAM_MODULE_BY_ID[moduleId];
  if (!mod) return EMPTY_FIELD;
  const entry = state.entries.find((e) => e.moduleId === moduleId);

  const parts: string[] = [];
  for (const eye of ['OD', 'OE'] as const) {
    const body = formatModuleEye(mod, entry, eye);
    if (body) parts.push(`${eye} ${body}`);
  }
  for (const { label, value } of formatModuleMonoFields(mod, entry)) {
    // Módulo só-mono (ex.: classificacao-glaucoma) sai só com o valor — o
    // rótulo já está na frase do laudo que carrega o token.
    parts.push(mod.fields.length === 1 ? value : `${label} ${value}`);
  }
  return parts.length ? parts.join(br) : EMPTY_FIELD;
}

/**
 * Substitui TODOS os tokens `{{ex:...}}` de `text` pelos valores de `state`.
 * Função pura e idempotente (2ª passada não acha mais token, vira no-op).
 * Token de módulo/campo inexistente ou sem valor → `EMPTY_FIELD`.
 */
export function resolveExamTags(text: string, state: EncounterExamsState): string {
  if (!text) return text;
  const br = looksLikeHtml(text) ? '<br>' : EYE_BREAK;
  return text.replace(
    EXAM_TAG_RE,
    (_match, moduleId: string, seg2: string | undefined, seg3: string | undefined) => {
      const mod = EXAM_MODULE_BY_ID[moduleId];
      if (!mod) return EMPTY_FIELD;

      // Forma 1 — módulo inteiro.
      if (!seg2) return formatExamModule(moduleId, state, br);

      const entry = state.entries.find((e) => e.moduleId === moduleId);
      let fieldKey: string;
      let eye: 'OD' | 'OE' | undefined;

      if (EYE_RE.test(seg2)) {
        // Forma 2 — primeiro campo + olho. (Nenhum fieldKey do catálogo é
        // "od"/"oe", então o casamento de olho aqui é seguro.)
        fieldKey = mod.fields[0]?.key ?? '';
        eye = seg2.toUpperCase() as 'OD' | 'OE';
      } else {
        // Forma 3 — campo nomeado, olho opcional.
        fieldKey = seg2;
        eye = seg3 ? (seg3.toUpperCase() as 'OD' | 'OE') : undefined;
      }

      const field = mod.fields.find((f) => f.key === fieldKey);
      if (!field) return EMPTY_FIELD;
      return readField(field, entry, eye, br) || EMPTY_FIELD;
    },
  );
}

/** True se `text` contém ao menos um token `{{ex:...}}`. Não consome o regex
 *  global (cria um novo a cada chamada) pra evitar bug de lastIndex. */
export function hasExamTags(text: string): boolean {
  return /\{\{ex:[a-z0-9-]+/i.test(text);
}

/**
 * Chips de "Exames do atendimento" — um por módulo do catálogo, inserindo o
 * token de módulo inteiro (forma 1). Formas 2/3 ficam reservadas pros seeds e
 * digitação avançada, pra não poluir a barra. Derivado de
 * `BUILTIN_EXAM_MODULES` pra nunca dessincronizar do catálogo.
 */
export const EXAM_TAG_PLACEHOLDERS: ExamTagDefinition[] = BUILTIN_EXAM_MODULES.map(
  (mod) => ({
    label: mod.label,
    tag: `{{ex:${mod.id}}}`,
    description: mod.hint
      ? `${mod.hint} — valores do atendimento`
      : `Valores de ${mod.label} do atendimento`,
  }),
);
